<template>
	<query-form v-model:query="query" :loading="loading" @apply="getReportData" @abort="clearQuery">
		<template #display>
			<div class="display-value">
				<div class="label">Month:</div>
				<div class="value">{{ formatDate(query.month, 'MMM YYYY') }}</div>
			</div>
		</template>
		<template #utils>
			<p-button
				v-tooltip.top="'Refresh'"
				icon="pi pi-refresh"
				aria-label="Refresh"
				class="mr-2"
				@click="getReportData(query, true)"
			/>
		</template>
		<template #form="form">
			<row>
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Month:</label>
							<div class="controls">
								<div class="field">
									<p-calendar v-model="form.query.month" date-format="M yy" view="month" label="" />
								</div>
							</div>
						</div>
					</div>
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Vertical:</label>
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.vertical_id"
										:options="$root.appStore.verticalOptions"
										option-label="label"
										option-value="value"
										placeholder="Filter by Vertical"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column max-width="300px">
					<div class="control-group">
						<div class="inner">
							<label class="control-label">Campaign Type:</label>
							<div class="controls">
								<div class="field">
									<p-multi-select
										v-model="form.query.filters.campaign_type"
										:options="campaign_type_options"
										option-label="label"
										option-value="value"
										placeholder="Filter by Campaign Type"
									/>
								</div>
							</div>
						</div>
					</div>
				</column>
				<gutter size="40px" />
				<column></column>
			</row>
		</template>
	</query-form>
	<gutter size="10px" />
	<div class="card padded">
		<div class="data-table">
			<p-data-table
				:value="report_data"
				ref="am_leaderboard_table"
				data-key="account_manager_id"
				class="am-leaderboard-table"
				sort-field="rank"
				:sort-order="1"
				scrollable
				:frozen-value="totalsRow"
				:loading="loading"
			>
				<template #empty>
					<div class="dim">No Data</div>
				</template>
				<template #loading>
					<line-loader :show="loading" />
				</template>
				<p-column header="Rank" sortable field="rank" class="column-align-center">
					<template #body="row">{{ row.data.rank }}</template>
				</p-column>
				<p-column header="Sales Associate">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">
							<strong>Totals</strong>
						</template>
						<template v-else>
							<router-link :to="`/admin-users/${row.data.account_manager_id}`"
								><strong class="larger">{{ row.data.account_manager_name }}</strong></router-link
							><br />
							<div class="sub-value">{{ row.data.account_manager_id }}</div>
						</template>
					</template>
				</p-column>
				<p-column header="Accounts" sortable field="current.total_accounts" class="column-align-right">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">
							{{ round(row.data.total_accounts, 2) }}
						</template>
						<template v-else>
							{{ round(row.data.current.total_accounts, 2) }}<br />
							<div class="sub-value inline-flex align-items-center gap-5">
								<icon type="calendar-arrow-left" size="16px" /> {{ round(row.data.previous.total_accounts, 2) }}
							</div>
							<div
								class="sub-value"
								v-html="trend(row.data.current.total_accounts, row.data.previous.total_accounts)"
							/>
						</template>
					</template>
				</p-column>
				<p-column header="Active (%)" sortable field="current.active_percent" class="column-align-right">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">
							{{ row.data.active_accounts }} ({{ percentage(row.data.active_percent, 2) }})
						</template>
						<template v-else>
							{{ row.data.current.active_accounts }} ({{ percentage(row.data.current.active_percent, 2) }})<br />
							<div class="sub-value inline-flex align-items-center gap-5">
								<icon type="calendar-arrow-left" size="16px" /> {{ row.data.previous.active_accounts }} ({{
									percentage(row.data.previous.active_percent, 2)
								}})
							</div>
							<div
								class="sub-value"
								v-html="trend(row.data.current.active_accounts, row.data.previous.active_accounts)"
							/>
						</template>
					</template>
				</p-column>
				<p-column header="Spend" sortable field="current.spend" class="column-align-right">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">
							{{ currency(row.data.spend, 0) }}
						</template>
						<template v-else>
							{{ currency(row.data.current.spend, 0) }}<br />
							<div class="sub-value inline-flex align-items-center gap-5">
								<icon type="calendar-arrow-left" size="16px" /> {{ currency(row.data.previous.spend, 0) }}
							</div>
							<div class="sub-value" v-html="trend(row.data.current.spend, row.data.previous.spend)" />
						</template>
					</template>
				</p-column>
				<p-column header="Spend/Account" sortable field="current.spa" class="column-align-right">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">
							{{ currency(row.data.spa, 0) }}
						</template>
						<template v-else>
							{{ currency(row.data.current.spa, 0) }}<br />
							<div class="sub-value inline-flex align-items-center gap-5">
								<icon type="calendar-arrow-left" size="16px" /> {{ currency(row.data.previous.spa, 0) }}
							</div>
							<div class="sub-value" v-html="trend(row.data.current.spa, row.data.previous.spa)" />
						</template>
					</template>
				</p-column>
				<p-column header="Goal (%)" sortable field="goal_percent" class="column-align-right">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">
							{{ currency(row.data.spend_goal, 0) }}
						</template>
						<template v-else>
							{{ currency(row.data.spend_goal, 0) }}<br />
							<div class="sub-value inline-flex align-items-center gap-5">
								<icon type="gauge" size="16px" />
								{{ percentage(row.data.goal_percent, 2) }}
							</div>
						</template>
					</template>
				</p-column>
				<p-column header="Share" sortable field="current.marketshare" class="column-align-right">
					<template #body="row">
						<template v-if="row.data.account_manager_name === 'Totals'">100%</template>
						<template v-else>
							{{ percentage(marketshare(row.data.current.spend), 2) }}
						</template>
					</template>
				</p-column>
			</p-data-table>
		</div>
	</div>
</template>

<script>
import pCalendar from 'primevue/calendar';
import lineLoader from '@/components/elements/LineLoader.vue';
import radioGroup from '@/components/forms/RadioGroup.vue';
import queryForm from '@/components/widgets/QueryForm.vue';
import quickFilter from '@/components/widgets/QuickFilter.vue';
import { colorCell, currency, displayLabel, displaySelected, formatDate, percentage, trend } from '@/lib/Filters';
import { reportSALeaderboard } from '@GQL';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import { cloneDeep, round, sumBy } from 'lodash-es';

dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

export default {
	name: 'SALeaderboardTable',
	components: {
		lineLoader,
		pCalendar,
		queryForm,
		quickFilter,
		radioGroup,
	},
	data() {
		return {
			loading: false,
			query: {
				month: dayjs().toDate(),
				filters: {
					campaign_type: [],
					vertical_id: [],
				},
			},
			report_data: [],
			campaign_type_options: [
				{ label: 'Exclusive Data', value: 'exclusive_data' },
				{ label: 'Shared Data', value: 'shared_data' },
				{ label: 'Premium Listing', value: 'premium_listing' },
				{ label: 'Call', value: 'call' },
				{ label: 'Live Transfer', value: 'live_transfer' },
			],
		};
	},
	computed: {
		totalsRow() {
			const totals = {
				account_manager_name: 'Totals',
				total_accounts: 0,
				active_accounts: 0,
				active_percent: 0,
				spend: 0,
				spa: 0,
				spend_goal: 0,
			};

			totals.total_accounts = sumBy(this.report_data, (row) => {
				return row.current.total_accounts;
			});

			totals.active_accounts = sumBy(this.report_data, (row) => {
				return +row.current.active_accounts;
			});

			totals.active_percent = totals.total_accounts > 0 ? totals.active_accounts / totals.total_accounts : 0;

			totals.spend = sumBy(this.report_data, (row) => {
				return row.current.spend;
			});

			totals.spa = totals.total_accounts > 0 ? totals.spend / totals.total_accounts : 0;

			totals.spend_goal = sumBy(this.report_data, (row) => {
				return row.spend_goal;
			});

			return [totals];
		},
	},
	methods: {
		colorCell,
		currency,
		displayLabel,
		displaySelected,
		formatDate,
		percentage,
		round,
		trend,
		async getReportData(query, refresh) {
			const report_query = cloneDeep(query);
			report_query.month = dayjs(report_query.month).format('YYYY-MM');

			try {
				this.loading = true;
				const results = await reportSALeaderboard(report_query, refresh);

				// Add marketshare to each result
				this.report_data = results;
			} finally {
				this.loading = false;
			}

			// Scroll all the way right
			if (this.report_data.length) {
				this.$nextTick(() => {
					document.querySelector('.p-datatable-wrapper').scrollLeft =
						document.querySelector('.p-datatable-wrapper').scrollWidth;
				});
			}
		},
		clearQuery() {
			this.loading = false;
		},
		marketshare(value) {
			if (this.totalsRow[0].spend > 0) {
				return value / this.totalsRow[0].spend;
			}
			return 0;
		},
	},
	async mounted() {
		await this.getReportData(this.query);
	},
};
</script>

<style lang="less" scoped>
:deep(.p-datatable-wrapper) {
	overscroll-behavior-x: none !important;
}
</style>
